/**
 * Custom Toast Utilities
 *
 * Provides styled toast notifications with Material Icons
 * Supports dark mode via html.dark class detection
 */

import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloseIcon from '@mui/icons-material/Close';

const isDark = () => document.documentElement.classList.contains('dark');

/**
 * Custom close button component
 */
const CloseButton = ({ toastId }: { toastId: string }) => (
  <button
    onClick={() => toast.dismiss(toastId)}
    style={{
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '8px',
      opacity: 0.6,
      transition: 'opacity 0.2s',
      color: 'inherit',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
  >
    <CloseIcon sx={{ fontSize: 18 }} />
  </button>
);

// Light / Dark style pairs
const styles = {
  success: {
    light: { background: '#f0fdf4', border: '1px solid #86efac', color: '#166534' },
    dark:  { background: '#0f2419', border: '1px solid #22c55e40', color: '#86efac' },
  },
  error: {
    light: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' },
    dark:  { background: '#2a0f0f', border: '1px solid #ef444440', color: '#fca5a5' },
  },
  warning: {
    light: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' },
    dark:  { background: '#271e0a', border: '1px solid #f59e0b40', color: '#fde68a' },
  },
  info: {
    light: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' },
    dark:  { background: '#0c1629', border: '1px solid #3b82f640', color: '#93c5fd' },
  },
};

const base = { padding: '12px 16px', fontSize: '14px', fontWeight: '500' as const };

/**
 * Custom toast styles with Material Icons
 */
export const customToast = {
  /**
   * Success toast (green)
   */
  success: (message: string, options?: { id?: string; duration?: number }) => {
    const s = isDark() ? styles.success.dark : styles.success.light;
    return toast.success(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <span style={{ flex: 1 }}>{message}</span>
          <CloseButton toastId={t.id} />
        </div>
      ),
      {
        ...options,
        icon: <CheckCircleIcon sx={{ fontSize: 24, color: '#10b981' }} />,
        style: { ...base, ...s, maxWidth: '500px' },
      }
    );
  },

  /**
   * Error toast (red)
   */
  error: (message: string, options?: { id?: string; duration?: number }) => {
    const s = isDark() ? styles.error.dark : styles.error.light;
    return toast.error(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <span style={{ flex: 1 }}>{message}</span>
          <CloseButton toastId={t.id} />
        </div>
      ),
      {
        ...options,
        icon: <ErrorIcon sx={{ fontSize: 24, color: '#ef4444' }} />,
        style: { ...base, ...s, width: '380px' },
      }
    );
  },

  /**
   * Warning toast (yellow/amber)
   */
  warning: (message: string, options?: { id?: string; duration?: number }) => {
    const s = isDark() ? styles.warning.dark : styles.warning.light;
    return toast(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <span style={{ flex: 1 }}>{message}</span>
          <CloseButton toastId={t.id} />
        </div>
      ),
      {
        ...options,
        icon: <WarningIcon sx={{ fontSize: 24, color: '#f59e0b' }} />,
        style: { ...base, ...s, maxWidth: '500px' },
      }
    );
  },

  /**
   * Info toast (blue)
   */
  info: (message: string, options?: { id?: string; duration?: number }) => {
    const s = isDark() ? styles.info.dark : styles.info.light;
    return toast(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <span style={{ flex: 1 }}>{message}</span>
          <CloseButton toastId={t.id} />
        </div>
      ),
      {
        ...options,
        icon: <InfoIcon sx={{ fontSize: 24, color: '#3b82f6' }} />,
        style: { ...base, ...s, maxWidth: '500px' },
      }
    );
  },

  /**
   * Loading toast (blue with hourglass)
   */
  loading: (message: string, options?: { id?: string }) => {
    const s = isDark() ? styles.info.dark : styles.info.light;
    return toast.loading(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <span style={{ flex: 1 }}>{message}</span>
          <CloseButton toastId={t.id} />
        </div>
      ),
      {
        ...options,
        icon: <HourglassEmptyIcon sx={{ fontSize: 24, color: '#3b82f6' }} />,
        style: { ...base, ...s, maxWidth: '500px' },
      }
    );
  },
};
